import { AfterViewChecked, Component, DestroyRef, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SupportCasesApiService } from 'src/app/services/API/support/support-cases-api.service';
import { SupportListItem } from 'src/app/common/models/support/SupportListItem';
import { SupportThread } from 'src/app/common/models/support/SupportThread';
import { CommentAuthorType, SupportMessage } from 'src/app/common/models/support/SupportMessage';
import { environment } from 'src/environments/environment';

type ReplyOutcome =
  | { kind: 'sent'; newMessage: SupportMessage }
  | { kind: 'refetched'; thread: SupportThread }
  | { kind: 'refetchFailed' };

@Component({
    selector: 'app-support-tab',
    templateUrl: './support-tab.component.html',
    styleUrls: ['./support-tab.component.scss'],
    standalone: false
})
export class SupportTabComponent implements OnInit, OnChanges, AfterViewChecked {

  isCaseNumberEnabled = environment.IS_SUPPORT_CASE_NUMBER_ENABLED;

  @Input() openNewCaseRequestId: number | null = null;
  @Input() prefillMessage = '';

  view: 'list' | 'thread' = 'list';
  cases: SupportListItem[] = [];
  filteredCases: SupportListItem[] = [];
  isLoading = false;
  loadError = false;

  statusFilter: 'All' | 'Open' | 'Closed' = 'All';

  isNewCaseFormOpen = false;
  newCaseForm = new UntypedFormGroup({
    heading: new UntypedFormControl('', Validators.maxLength(120)),
    message: new UntypedFormControl('')
  });
  isCreating = false;
  createError = false;

  activeThread: SupportThread | null = null;
  threadLoadError = false;
  isThreadLoading = false;

  replyText = new UntypedFormControl('');
  isSendingReply = false;
  replyError = false;

  // Decorator-based (not the signal `viewChild()` query) because this component's logic
  // specs instantiate it directly via `new SupportTabComponent(...)`, bypassing Angular's
  // DI/view setup -- `viewChild()` calls inject() at field-initializer time and throws
  // (NG0203) outside that context, while `@ViewChild` tolerates it (query just stays unset).
  @ViewChild('replyTextarea') private replyTextareaRef?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('newCaseMessageTextarea') private newCaseMessageTextareaRef?: ElementRef<HTMLTextAreaElement>;

  // The popup's textareas are compact, so growth is capped well short of a full-page
  // composer -- enough to review a few lines without the box taking over the popup.
  private static readonly MAX_TEXTAREA_HEIGHT_PX = 140;

  // Which textarea the expand-to-compose modal is currently editing, or null when collapsed.
  // Both the reply box and the new-case message field share the one modal/icon pattern --
  // composerConfig picks which FormControl and copy it's acting on for whichever one is open.
  // Neither carries a character limit (per product decision).
  composerTarget: 'reply' | 'newCase' | null = null;

  get composerOpen(): boolean {
    return this.composerTarget !== null;
  }

  get composerConfig(): { control: UntypedFormControl; titleKey: string; placeholderKey: string } {
    return this.composerTarget === 'newCase'
      ? {
        control: this.newCaseForm.get('message') as UntypedFormControl,
        titleKey: 'support_popup.case.lbl_composer_title_message',
        placeholderKey: 'support_popup.case.lbl_message_placeholder'
      }
      : {
        control: this.replyText,
        titleKey: 'support_popup.case.lbl_composer_title',
        placeholderKey: 'support_popup.case.lbl_reply_placeholder'
      };
  }

  // The inline textarea is destroyed/recreated by *ngIf when the composer opens/closes
  // (see onCloseComposer), so the explicit height set by onTextareaInput is lost along with
  // the old element -- the fresh one starts at the default CSS height regardless of how
  // much text it holds. This remembers which inline textarea needs ngAfterViewChecked to
  // re-run the same sizing logic once it's actually back in the DOM.
  private pendingInlineResizeTarget: 'reply' | 'newCase' | null = null;

  isRatingModalOpen = false;
  pendingRating: number | null = null;
  isClosingCase = false;
  closeError = false;

  private loadCasesSubscription?: Subscription;
  private loadThreadSubscription?: Subscription;

  constructor(
    private supportCasesApi: SupportCasesApiService,
    private destroyRef: DestroyRef
  ) { }

  ngOnInit(): void {
    this.loadCases();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['openNewCaseRequestId'] && this.openNewCaseRequestId != null) {
      this.openNewCaseForm(this.prefillMessage);
    }
  }

  private loadCases(): void {
    this.isLoading = true;
    this.loadError = false;
    this.threadLoadError = false;
    this.loadCasesSubscription?.unsubscribe();
    this.loadCasesSubscription = this.supportCasesApi.getOwnCases().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (cases) => {
        this.cases = this.sortOpenFirst(cases);
        this.applyStatusFilter();
        this.isLoading = false;
      },
      error: () => {
        this.cases = [];
        this.applyStatusFilter();
        this.isLoading = false;
        this.loadError = true;
      }
    });
  }

  private sortOpenFirst(cases: SupportListItem[]): SupportListItem[] {
    return [...cases].sort((a, b) => (a.status === b.status ? 0 : a.status === 'Open' ? -1 : 1));
  }

  private applyStatusFilter(): void {
    this.filteredCases = this.statusFilter === 'All'
      ? this.cases
      : this.cases.filter((c) => c.status === this.statusFilter);
  }

  public setStatusFilter(filter: 'All' | 'Open' | 'Closed'): void {
    this.statusFilter = filter;
    this.applyStatusFilter();
  }

  public trackBySupportId(_index: number, c: SupportListItem): string {
    return c.supportId;
  }

  public toggleNewCaseForm(): void {
    this.isNewCaseFormOpen = !this.isNewCaseFormOpen;
    if (!this.isNewCaseFormOpen) {
      if (this.composerTarget === 'newCase') this.composerTarget = null;
      this.resetNewCaseForm();
    }
  }

  public openNewCaseForm(prefillMessage = ''): void {
    this.view = 'list';
    this.activeThread = null;
    this.newCaseForm.get('heading')?.setValue('');
    this.newCaseForm.get('message')?.setValue(prefillMessage);
    this.createError = false;
    this.isNewCaseFormOpen = true;
    this.composerTarget = null;
  }

  private resetNewCaseForm(): void {
    this.newCaseForm.get('heading')?.setValue('');
    this.newCaseForm.get('message')?.setValue('');
    this.createError = false;
    this.resetTextareaHeight(this.newCaseMessageTextareaRef);
  }

  public isNewCaseFormValid(): boolean {
    const heading = this.newCaseForm.get('heading')?.value || '';
    const message = this.newCaseForm.get('message')?.value || '';
    return heading.trim() !== '' && message.trim() !== '' && this.newCaseForm.valid;
  }

  public submitNewCase(): void {
    if (!this.isNewCaseFormValid()) {
      return;
    }

    const heading = this.newCaseForm.get('heading')?.value.trim();
    const message = this.newCaseForm.get('message')?.value.trim();

    this.isCreating = true;
    this.createError = false;

    this.supportCasesApi.createCase(heading, message).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isCreating = false;
        this.isNewCaseFormOpen = false;
        this.composerTarget = null;
        this.resetNewCaseForm();
        this.setStatusFilter('All');
        this.loadCases();
      },
      error: () => {
        this.isCreating = false;
        this.createError = true;
      }
    });
  }

  public openCase(supportId: string): void {
    if (this.isThreadLoading) {
      return;
    }

    this.isThreadLoading = true;
    this.threadLoadError = false;
    this.loadThreadSubscription?.unsubscribe();
    this.loadThreadSubscription = this.supportCasesApi.getThread(supportId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (thread) => {
        this.isThreadLoading = false;
        this.activeThread = thread;
        this.view = 'thread';
        this.clearUnreadReply(supportId);
        this.resetReplyState();
        this.setReplyEnabled(thread.status !== 'Closed');
      },
      error: () => {
        this.isThreadLoading = false;
        this.threadLoadError = true;
      }
    });
  }

  private clearUnreadReply(supportId: string): void {
    this.cases = this.cases.map((c) =>
      c.supportId === supportId ? { ...c, hasUnreadReply: false } : c
    );
    this.applyStatusFilter();
  }

  private setReplyEnabled(enabled: boolean): void {
    if (enabled) {
      this.replyText.enable();
    } else {
      this.replyText.disable();
    }
  }

  private resetReplyState(): void {
    this.replyError = false;
    this.replyText.reset('');
    this.composerTarget = null;
  }

  public onExpandComposer(target: 'reply' | 'newCase' = 'reply'): void {
    this.composerTarget = target;
  }

  public onCloseComposer(): void {
    this.pendingInlineResizeTarget = this.composerTarget;
    this.composerTarget = null;
  }

  public ngAfterViewChecked(): void {
    if (!this.pendingInlineResizeTarget) return;
    const ref = this.pendingInlineResizeTarget === 'newCase' ? this.newCaseMessageTextareaRef : this.replyTextareaRef;
    if (ref) {
      this.onTextareaInput(ref.nativeElement);
      this.pendingInlineResizeTarget = null;
    }
  }

  // Grows a textarea with its content so a longer reply/message stays reviewable without
  // scrolling inside a tiny box. Only grows while there's text -- scrollHeight reflects
  // the full content height regardless of the current explicit height, so there's no
  // need to reset the height first, which would otherwise overwrite a height the user
  // set by dragging the resize handle. Once the box is emptied, resetTextareaHeight()
  // drops the explicit height so it returns to its default size.
  public onTextareaInput(textarea: HTMLTextAreaElement): void {
    if (!textarea.value) {
      textarea.style.height = '';
      return;
    }
    const desiredHeight = Math.min(textarea.scrollHeight, SupportTabComponent.MAX_TEXTAREA_HEIGHT_PX);
    if (desiredHeight > textarea.clientHeight) {
      textarea.style.height = `${desiredHeight}px`;
    }
  }

  private resetTextareaHeight(ref?: ElementRef<HTMLTextAreaElement>): void {
    const textarea = ref?.nativeElement;
    if (textarea) textarea.style.height = '';
  }

  public backToList(): void {
    this.loadThreadSubscription?.unsubscribe();
    this.view = 'list';
    this.activeThread = null;
    this.statusFilter = 'All';
    this.applyStatusFilter();
    this.resetReplyState();
    this.isRatingModalOpen = false;
    this.pendingRating = null;
    this.closeError = false;
    this.loadCases();
  }

  public sendReply(): void {
    const message = (this.replyText.value || '').trim();
    if (!message || !this.activeThread || this.activeThread.status === 'Closed' || this.isSendingReply) {
      return;
    }

    const supportId = this.activeThread.supportId;
    this.isSendingReply = true;
    this.replyError = false;
    this.replyText.disable();

    this.supportCasesApi.postReply(supportId, message).pipe(
      map((newMessage): ReplyOutcome => ({ kind: 'sent', newMessage })),
      catchError((): Observable<ReplyOutcome> => {
        this.replyError = true;
        return this.supportCasesApi.getThread(supportId).pipe(
          map((thread): ReplyOutcome => ({ kind: 'refetched', thread })),
          catchError(() => of<ReplyOutcome>({ kind: 'refetchFailed' }))
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((outcome) => {
      this.isSendingReply = false;
      if (!this.activeThread || this.activeThread.supportId !== supportId) return;

      switch (outcome.kind) {
        case 'sent':
          this.activeThread = { ...this.activeThread, messages: [...this.activeThread.messages, outcome.newMessage] };
          this.replyText.setValue('');
          this.resetTextareaHeight(this.replyTextareaRef);
          this.composerTarget = null;
          this.setReplyEnabled(true);
          break;
        case 'refetched':
          this.activeThread = outcome.thread;
          this.setReplyEnabled(outcome.thread.status !== 'Closed');
          break;
        case 'refetchFailed':
          this.setReplyEnabled(true);
          break;
      }
    });
  }

  // Record<CommentAuthorType, string> forces every enum member to be mapped here --
  // adding a new CommentAuthorType without updating this map is a compile error,
  // instead of silently falling through to 'admin' the way a string-typed if-chain
  // would. AI currently maps to 'admin' deliberately: there's no distinct .ai bubble
  // style in this component yet, so this preserves today's actual rendered behavior.
  private static readonly AUTHOR_CLASSES: Record<CommentAuthorType, string> = {
    [CommentAuthorType.Member]: 'member',
    [CommentAuthorType.System]: 'system',
    [CommentAuthorType.Admin]: 'admin',
    [CommentAuthorType.AI]: 'admin',
  };

  public authorClass(authorType: CommentAuthorType): string {
    return SupportTabComponent.AUTHOR_CLASSES[authorType] ?? 'admin';
  }

  public openRatingModal(): void {
    if (!this.activeThread || this.activeThread.status === 'Closed') {
      return;
    }
    this.pendingRating = null;
    this.closeError = false;
    this.isRatingModalOpen = true;
  }

  public cancelRatingModal(): void {
    this.isRatingModalOpen = false;
    this.pendingRating = null;
  }

  public selectRating(value: number): void {
    this.pendingRating = value;
  }

  public confirmClose(): void {
    if (this.pendingRating === null || !this.activeThread || this.isClosingCase) {
      return;
    }

    const supportId = this.activeThread.supportId;
    this.isClosingCase = true;
    this.closeError = false;
    this.supportCasesApi.close(supportId, this.pendingRating).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.isClosingCase = false;
        if (!this.activeThread || this.activeThread.supportId !== supportId) return;

        this.activeThread = { ...this.activeThread, status: result.status, rating: result.rating, closedOn: result.closedOn };
        this.isRatingModalOpen = false;
        this.setReplyEnabled(false);
      },
      error: () => {
        this.isClosingCase = false;
        this.closeError = true;
      }
    });
  }
}
